import React, { useState } from "react";
import TopMostPortal from "../../components/ui/utils/Portals";
import { IDatabaseLineItem, LineItemMetadata } from "./LineItemDBCreationTool/types";
import { doc, getFirestore, updateDoc } from "firebase/firestore";
import SharedUtils from "../../shared/common/SharedUtils";
import { toast } from "react-toastify";

// Re-export for backward compatibility with any code importing LineItemMetadata from this file
export type { LineItemMetadata } from "./LineItemDBCreationTool/types";

interface LineItemInfoModalProps {
	lineItem: IDatabaseLineItem;
	onClose: () => void;
}

const LineItemInfoModal: React.FC<LineItemInfoModalProps> = ({ lineItem, onClose }) => {
	const [uniqueShortName, setUniqueShortName] = useState(lineItem.metadata?.uniqueShortName || "");
	const [keyPhrases, setKeyPhrases] = useState(lineItem.metadata?.keyPhrases || "");
	const [explanation, setExplanation] = useState(lineItem.metadata?.explanation || "");
	const [units, setUnits] = useState(lineItem.metadata?.units || "");
	const [defaultQuantity, setDefaultQuantity] = useState(lineItem.metadata?.defaultQuantity || "");
	const [saving, setSaving] = useState(false);

	const handleSave = async () => {
		setSaving(true);
		try {
			const firestore = getFirestore();
			const docRef = doc(firestore, SharedUtils.getDbPath("tools"), lineItem.id);

			const metadata: LineItemMetadata = {};
			if (uniqueShortName.trim()) metadata.uniqueShortName = uniqueShortName.trim();
			if (keyPhrases.trim()) metadata.keyPhrases = keyPhrases.trim();
			if (explanation.trim()) metadata.explanation = explanation.trim();
			if (units.trim()) metadata.units = units.trim();
			if (defaultQuantity.trim()) metadata.defaultQuantity = defaultQuantity.trim();

			await updateDoc(docRef, {
				metadata: metadata,
			});

			toast.success(
				<div className="flex items-center">
					<div className="mr-4 ml-4 text-gray-900 pr-16">Metadata saved successfully</div>
				</div>
			);
			onClose();
		} catch (error) {
			toast.error(
				<div className="flex items-center">
					<div className="mr-4 ml-4 text-gray-900 pr-16">Error saving metadata</div>
				</div>
			);
		} finally {
			setSaving(false);
		}
	};
	return (
		<TopMostPortal>
			<div className="w-[900px] min-h-[200px] bg-white rounded-xl shadow flex-col justify-between items-center flex">
				<div className="self-stretch bg-white flex-col justify-start items-center flex">
					<div className="self-stretch px-6 pt-6 bg-white justify-between items-start gap-4 inline-flex">
						<div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
							<div className="self-stretch text-gray-900 text-lg font-semibold font-['Hanken Grotesk'] leading-7">
								{lineItem.originalLineItem}
							</div>
							<div className="text-gray-500 text-sm font-normal font-['Hanken Grotesk'] leading-tight">
								id: {lineItem.id}
							</div>
							<div className="text-gray-600 text-sm font-normal font-['Hanken Grotesk'] leading-tight mt-1">
								The following fields will help provide context in processing line items. Fill out as many or as little as you need.
							</div>
						</div>
						<div
							className="cursor-pointer"
							onClick={onClose}
						>
							<img src="/assets/ui/x-close.svg" className="w-5 h-5 relative" />
						</div>
					</div>
					<div className="self-stretch h-5" />
				</div>

				<div className="self-stretch px-6 py-6 flex-col justify-start items-start gap-4 flex">
					<div className="self-stretch flex-col justify-start items-start gap-2 flex">
						<label className="text-gray-700 text-sm font-medium font-['Hanken Grotesk'] leading-tight">
							Unique Short Name
						</label>
						<input
							type="text"
							value={uniqueShortName}
							onChange={(e) => setUniqueShortName(e.target.value)}
							className="self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-900 text-base font-normal font-['Hanken Grotesk'] leading-normal focus:outline-none focus:border-blue-500"
							placeholder="Enter unique short name"
						/>
					</div>

					<div className="self-stretch flex-col justify-start items-start gap-2 flex">
						<label className="text-gray-700 text-sm font-medium font-['Hanken Grotesk'] leading-tight">
							Key Phrases/Examples
						</label>
						<textarea
							value={keyPhrases}
							onChange={(e) => setKeyPhrases(e.target.value)}
							rows={3}
							className="self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-900 text-base font-normal font-['Hanken Grotesk'] leading-normal focus:outline-none focus:border-blue-500 resize-vertical"
							placeholder="Enter key phrases or examples"
						/>
					</div>

					<div className="self-stretch flex-col justify-start items-start gap-2 flex">
						<label className="text-gray-700 text-sm font-medium font-['Hanken Grotesk'] leading-tight">
							Explanation/Construction Knowledge
						</label>
						<textarea
							value={explanation}
							onChange={(e) => setExplanation(e.target.value)}
							rows={4}
							className="self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-900 text-base font-normal font-['Hanken Grotesk'] leading-normal focus:outline-none focus:border-blue-500 resize-vertical"
							placeholder="Enter explanation or construction knowledge"
						/>
					</div>

					<div className="self-stretch flex gap-4">
						<div className="flex-1 flex-col justify-start items-start gap-2 flex">
							<label className="text-gray-700 text-sm font-medium font-['Hanken Grotesk'] leading-tight">
								Unit/Units
							</label>
							<input
								type="text"
								value={units}
								onChange={(e) => setUnits(e.target.value)}
								className="self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-900 text-base font-normal font-['Hanken Grotesk'] leading-normal focus:outline-none focus:border-blue-500"
								placeholder="e.g. SF, LF, EA"
							/>
						</div>

						<div className="flex-1 flex-col justify-start items-start gap-2 flex">
							<label className="text-gray-700 text-sm font-medium font-['Hanken Grotesk'] leading-tight">
								Default Quantity
							</label>
							<input
								type="text"
								value={defaultQuantity}
								onChange={(e) => setDefaultQuantity(e.target.value)}
								className="self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 text-gray-900 text-base font-normal font-['Hanken Grotesk'] leading-normal focus:outline-none focus:border-blue-500"
								placeholder="e.g. 1, 100, F, PF"
							/>
						</div>
					</div>
				</div>

				<div className="self-stretch h-[68px] flex-col justify-center items-start flex">
					<div className="self-stretch px-6 pb-6 justify-end items-center gap-3 inline-flex">
						<div
							className="px-[18px] py-2.5 bg-white rounded-lg border border-gray-200 justify-center items-center gap-2 flex cursor-pointer"
							onClick={onClose}
						>
							<div className="text-slate-700 text-base font-semibold font-['Hanken Grotesk'] leading-normal">Close</div>
						</div>
						<div
							className={`px-[18px] py-2.5 rounded-lg shadow border justify-center items-center gap-2 flex cursor-pointer ${
								saving ? "bg-gray-400 border-gray-400" : "bg-blue-900 border-blue-900"
							}`}
							onClick={saving ? undefined : handleSave}
						>
							<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal">
								{saving ? "Saving..." : "Save"}
							</div>
						</div>
					</div>
				</div>
			</div>
		</TopMostPortal>
	);
};

export default LineItemInfoModal;
